import { Grid } from '@mui/material';
import { Formik, Field } from 'formik';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroYesNo,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { DealPartnerContact } from '../../../shared/models';
import { FormikUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface EditPartnerContactFormProps {
	dataRoomGuid: string | undefined;
	contact: DealPartnerContact | null;
	close: () => void;
}

export const EditPartnerContactForm: React.FC<EditPartnerContactFormProps> = ({ dataRoomGuid, contact, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const editPartnerContactMutation = useMutation(
		async ({ values }: EditPartnerContactFormVariables) => {
			if (!values.roomAccess) {
				const { data, error } = await getApiResponseData<void>(
					api.delete(CerebroApiEndpoints.DataRoomPartnerContacts.RemoveAccess(dataRoomGuid, contact?.guid))
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Successfully updated deal partner contact room access.', {
					variant: 'success',
				});
				client.invalidateQueries(QueryKeys.DataRoomPartnerContacts.Root(dataRoomGuid));
				close();
			},

			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={contact != null} onClose={() => close()}>
			<CerebroHeading> Update Deal Partner Contact Room Access</CerebroHeading>

			<Formik
				initialValues={{
					roomAccess: true,
				}}
				onSubmit={FormikUtils.MutateOnSubmit(editPartnerContactMutation.mutate)}
			>
				{({ isSubmitting, handleSubmit }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={8}>
									<CerebroLabel id="companyContact">Deal Partner Contact</CerebroLabel>
									<Field
										id="companyContact"
										as={CerebroInput}
										name="companyContact"
										disabled={true}
										placeholder={`${contact?.firstName} ${contact?.lastName}`}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroLabel id="roomAccess">Room Access</CerebroLabel>
									<Field
										id="roomAccess"
										as={CerebroYesNo}
										name="roomAccess"
										disabled={isSubmitting}
									/>
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type EditPartnerContactFormVariables = FormikUtils.MutateOnSubmitVariables<EditPartnerContactFormValues>;
interface EditPartnerContactFormValues {
	roomAccess: boolean;
}
